/* src/components/Header/Header.tsx */
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import T from '@data/text/header.json';

import SvgIcon from '@components/shared/icon/svgIcon/SvgIcon';
import { ROUTES } from '@configs/routeConstants';

import s from './Header.module.scss';

import SidebarToggle from '../buttons/sidebarToggle/SidebarToggle';
import Sidebar from '../sidebar/Sidebar';
import AccountButton from '../buttons/accountButton/AccountButton';
import { useCompanyTheme } from '@hooks/useCompanyTheme';
import useMetrics from '@hooks/useMetrics';
import { METRIC_EVENTS, METRIC_EVENTS_TRIGGERS } from '@configs/metrics/events';
import { getCurrentThemeFromDom, getThemeAsset, normalizeTheme } from '@configs/themeAssets';
import { isMentalHealthEnabled } from '@configs/features';
const isMobile = () => window.matchMedia('(max-width: 768px)').matches;
export const Header: React.FC = () => {
  const location = useLocation();
  const { trackEvent } = useMetrics();
  const [mobile, setMobile] = useState(false);

  const [open, setOpen] = useState(false);
  const openSb = () => setOpen(true);
  const closeSb = () => setOpen(false);

  const handleMainPageClick = () =>
    trackEvent(METRIC_EVENTS.main_page, METRIC_EVENTS_TRIGGERS.header);
  const handleContactsClick = () =>
    trackEvent(METRIC_EVENTS.contacts_page, METRIC_EVENTS_TRIGGERS.header);
  const handleChapterChoose = () =>
    trackEvent(METRIC_EVENTS.my_health_chapter_choose, METRIC_EVENTS_TRIGGERS.header, {
      chapter_name: METRIC_EVENTS_TRIGGERS.myHealth,
    });

  const handleMenuToggle = () => {
    trackEvent(METRIC_EVENTS.main_menu_chapter_button, METRIC_EVENTS_TRIGGERS.header);
    openSb();
  };

  useEffect(() => {
    const handler = () => setMobile(isMobile());
    handler();

    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const company = useCompanyTheme();

  const theme = getCurrentThemeFromDom() ?? normalizeTheme(company) ?? null;

  const isIngos = theme === 'ingosstrakh';
  const logoAriaLabel = T.logoAltByTheme[isIngos ? 'ingosstrakh' : 'default'];

  const srcLogo = getThemeAsset(theme, 'icon-header-logo', 'icon-logo');
  const isMentalHealthPath =
    isMentalHealthEnabled && location.pathname.startsWith(ROUTES.myHealth.mentalHealth);
  const isMyHealthPath = location.pathname.startsWith(ROUTES.myHealth.root);

  if (!srcLogo) return null;

  return (
    <>
      <header className={s.header}>
        <div className={s.container}>
          <Link
            to={ROUTES.root}
            aria-label={logoAriaLabel}
            className={s.iconLogo}
            onClick={handleMainPageClick}
          >
            <SvgIcon
              name={srcLogo}
              width={mobile ? 56 : 80}
              height={mobile ? 56 : 75}
              viewBox={mobile ? '0 0 77 77' : '0 0 80 75'}
              className={s.iconLogo}
            />
          </Link>
          <div className={s.navContainer}>
            <nav className={s.nav}>
              <Link
                to={ROUTES.root}
                className={s.navLink}
                data-active={location.pathname === ROUTES.root}
              >
                {T.nav.home}
              </Link>
              <Link
                to={ROUTES.myHealth.root}
                className={s.navLink}
                data-active={isMyHealthPath && !isMentalHealthPath}
                onClick={handleChapterChoose}
              >
                {T.nav.myHealth}
              </Link>
              {isMentalHealthEnabled && (
                <Link
                  to={ROUTES.myHealth.mentalHealth}
                  className={s.navLink}
                  data-active={isMentalHealthPath}
                  onClick={handleChapterChoose}
                >
                  {T.nav.mentalHealth}
                </Link>
              )}
              <Link
                to={ROUTES.contacts}
                data-active={location.pathname === ROUTES.contacts}
                className={s.navLink}
                onClick={handleContactsClick}
              >
                {T.nav.contacts}
              </Link>
            </nav>
            <div>
              <AccountButton labelSigned={T.account.link} labelUnsigned={T.account.signIn} />
            </div>
            <SidebarToggle onClick={handleMenuToggle} />
          </div>
        </div>
      </header>

      <Sidebar isOpen={open} onClose={closeSb} title={T.sidebar.title}></Sidebar>
    </>
  );
};
